import React, { Fragment, useMemo, useCallback, CSSProperties } from 'react';
import filter from 'lodash/filter';

import {
  IsFetched,
  ErrorMessage,
  FetchItemsLoadMoreItems
} from '../../../../../types';
import { TeamNanoID } from '../../../../teams/teamsTypes';
import {
  ProductNanoID,
  FetchProductsLimit,
  FetchProductsPage,
  UpdateProductCacheAction,
  OnProductSimilarSearchAction,
  ChangeProductsFiltersFunc,
  FetchProductsFilters
} from '../../../../products/productsTypes';
import {
  ThreeDStockProductsListProducts,
  ThreeDStockProductsListProduct,
  ThreeDStockProductsListProductsSet
} from './ThreeDStockProductsList.types';
import {
  FetchProductsSetsCacheKeys,
  UpdateProductsSetCacheAction
} from '../../../../productsSets/productsSetsTypes';

import { ThreeDStockProductsListItem } from '../ThreeDStockProductsListItem';
import { ThreeDStockProductsListLightboxTitle } from './components/ThreeDStockProductsListLightboxTitle';
import { ThreeDStockProductsListLightboxBottomButtons } from './components/ThreeDStockProductsListLightboxBottomButtons';

import { AlertMessage } from '../../../../../helpers/AlertMessage';
import { Loading } from '../../../../../helpers/Loading';
import { NoResults } from '../../../../../helpers/NoResults';
import {
  LightboxWrapper,
  useLightboxWrapper,
  LightboxRenderImageTitle,
  LightboxRenderCustomButtons
} from '../../../../../helpers/LightboxWrapper';
import { LoadingGridSkeleton } from '../../../../../helpers/LoadingGridSkeleton';
import { LoadMoreButtonHelper } from '../../../../../helpers/buttons/LoadMoreButtonHelper';

import { Files } from '../../../../../utils/Files';

import { ProductsPermissions } from '../../../../products/productsConstants';

export type ThreeDStockProductsListProps = {
  cellMinSize: number;
  onProductEditButtonMouseEnter?: (productNanoId: ProductNanoID) => void;
  products: ThreeDStockProductsListProducts;
  productsError: ErrorMessage;
  productsFetched: IsFetched;
  productsIsPlaceholderData: boolean;
  productsTotalCount: number;
  updateProductCache?: UpdateProductCacheAction<ThreeDStockProductsListProduct>;
  hasNextProductsPage: boolean;
  productsPage: FetchProductsPage;
  productsLimit: FetchProductsLimit;
  loadMoreProducts: FetchItemsLoadMoreItems;
  productsFetchingNextPage: boolean;
  productsSet: ThreeDStockProductsListProductsSet | null;
  changeProductsFilters: ChangeProductsFiltersFunc;
  productsFilters: FetchProductsFilters;
  productsSetCacheKeys?: FetchProductsSetsCacheKeys;
  updateProductsSetCache: UpdateProductsSetCacheAction<ThreeDStockProductsListProductsSet>;
  onProductSimilarSearchFilter?: OnProductSimilarSearchAction;
  isMyLibrary?: boolean;
  companyNanoId?: TeamNanoID;
  downloadButtonAction: string;
  downloadSeparatelyButtonAction: string;
  findSimilarButtonAction: string;
  editButtonAction: string;
};

function ThreeDStockProductsList({
  cellMinSize,
  onProductEditButtonMouseEnter,
  products,
  productsError,
  productsFetched,
  productsIsPlaceholderData,
  productsTotalCount,
  updateProductCache,
  hasNextProductsPage,
  productsPage,
  productsLimit,
  loadMoreProducts,
  productsFetchingNextPage,
  productsSet,
  changeProductsFilters,
  productsFilters,
  updateProductsSetCache,
  productsSetCacheKeys,
  onProductSimilarSearchFilter,
  companyNanoId,
  isMyLibrary,
  downloadButtonAction,
  downloadSeparatelyButtonAction,
  findSimilarButtonAction,
  editButtonAction
}: ThreeDStockProductsListProps) {
  const productsInLightbox = useMemo<ThreeDStockProductsListProducts>(() => {
    return filter<ThreeDStockProductsListProduct>(
      products,
      (product) => product.image?.file && Files.isImage(product.image.file)
    );
  }, [products]);

  const {
    handleLightboxClose,
    handleLightboxNext,
    handleLightboxOpen,
    handleLightboxOpenOnSlide,
    handleLightboxPrev,
    index,
    imagesCount,
    imageItem,
    lightBoxOpened,
    mainSrc,
    prevSrc,
    nextSrc
  } = useLightboxWrapper({
    items: productsInLightbox
  });

  const renderImageTitle = useCallback<LightboxRenderImageTitle>(
    ({ index }) => {
      const product = productsInLightbox[index];
      if (!product) {
        return null;
      }

      return <ThreeDStockProductsListLightboxTitle product={product} />;
    },
    [productsInLightbox]
  );

  const renderLightboxButtons = useCallback<LightboxRenderCustomButtons>(
    ({ index }) => {
      const product = productsInLightbox[index];
      if (!product) {
        return [];
      }

      return (
        <ThreeDStockProductsListLightboxBottomButtons
          product={product}
          productsSet={productsSet}
          productsSetCacheKeys={productsSetCacheKeys}
          updateProductsSetCache={updateProductsSetCache}
          updateProductCache={updateProductCache}
        />
      );
    },
    [
      productsInLightbox,
      productsSet,
      productsSetCacheKeys,
      updateProductsSetCache,
      updateProductCache
    ]
  );

  const wrapperStyle = useMemo(() => {
    return {
      '--tw-grid-cell-min-size': `${cellMinSize}px`
    };
  }, [cellMinSize]);

  return (
    <Fragment>
      <AlertMessage addClassName="m-4" message={productsError} />
      <LoadingGridSkeleton loaded={productsFetched}>
        <Loading loaded={!productsIsPlaceholderData} addClass="self-center" />
        {!productsIsPlaceholderData && productsTotalCount === 0 && (
          <NoResults addErrorClassName="m-4" />
        )}
        {!productsIsPlaceholderData && productsTotalCount > 0 && (
          <div className="flex flex-col space-y-2">
            <div
              className="grid grid-cols-auto-fill auto-rows-min gap-5"
              style={wrapperStyle as CSSProperties}
            >
              {products.map((product) => (
                <ThreeDStockProductsListItem
                  key={product.uuid}
                  product={product}
                  onProductEditButtonMouseEnter={onProductEditButtonMouseEnter}
                  onLightboxOpen={handleLightboxOpenOnSlide}
                  productsSet={productsSet}
                  updateProductsSetCache={updateProductsSetCache}
                  updateProductCache={updateProductCache}
                  productsSetCacheKeys={productsSetCacheKeys}
                  onProductSimilarSearchFilter={onProductSimilarSearchFilter}
                  companyNanoId={companyNanoId}
                  isMyLibrary={isMyLibrary}
                  changeProductsFilters={changeProductsFilters}
                  productsFilters={productsFilters}
                  downloadButtonAction={downloadButtonAction}
                  downloadSeparatelyButtonAction={
                    downloadSeparatelyButtonAction
                  }
                  findSimilarButtonAction={findSimilarButtonAction}
                  editButtonAction={editButtonAction}
                />
              ))}
            </div>

            {hasNextProductsPage && (
              <LoadMoreButtonHelper
                action={
                  ProductsPermissions.READ_PRODUCTS_LOAD_MORE_BUTTON_ITEMS_COUNT
                }
                dataGa="products-load-more-button"
                itemsTotalCount={productsTotalCount}
                page={productsPage}
                limit={productsLimit}
                loadMoreItems={loadMoreProducts}
                itemsFetchingNextPage={productsFetchingNextPage}
              />
            )}
          </div>
        )}
      </LoadingGridSkeleton>
      <LightboxWrapper
        handleLightboxClose={handleLightboxClose}
        handleLightboxNext={handleLightboxNext}
        handleLightboxOpen={handleLightboxOpen}
        handleLightboxPrev={handleLightboxPrev}
        index={index}
        imagesCount={imagesCount}
        imageItem={imageItem}
        lightBoxOpened={lightBoxOpened}
        mainSrc={mainSrc}
        nextSrc={nextSrc}
        prevSrc={prevSrc}
        renderImageTitle={renderImageTitle}
        renderCustomButtons={renderLightboxButtons}
        withFullScreenButton
      />
    </Fragment>
  );
}

export default ThreeDStockProductsList;
